/**
 * Color resolution for tags + sidebar.
 *
 * Colors live on each `DomainConfig` (server-stored, admin-editable). This
 * module just provides lookup helpers + the default pair when a label isn't
 * mapped to any known domain.
 */

import type { DomainConfig, DomainConfigMap } from '../hooks/useDomains';

export interface DomainColorPair {
  outline: string; // border + accent
  fill: string; // pill background
}

/** Neutral fallback for labels that don't map to a known domain. */
export const DEFAULT_PAIR: DomainColorPair = {
  outline: '#6b7280', // gray-500
  fill: '#f3f4f6', // gray-100
};

/** Resolve by domain id from the live domain map. */
export function getDomainColors(
  domains: DomainConfigMap | undefined,
  domainId: string | undefined
): DomainColorPair | null {
  if (!domains || !domainId) return null;
  const cfg = domains[domainId];
  return fromConfig(cfg);
}

/** Resolve by label name (case-insensitive match against each domain's labelName). */
export function getLabelColors(
  domains: DomainConfigMap | undefined,
  labelName: string | undefined
): DomainColorPair | null {
  if (!domains || !labelName) return null;
  const lower = labelName.toLowerCase();
  for (const cfg of Object.values(domains)) {
    if (cfg.labelName?.toLowerCase() === lower) {
      return fromConfig(cfg);
    }
  }
  return null;
}

function fromConfig(cfg: DomainConfig | undefined): DomainColorPair | null {
  if (!cfg) return null;
  if (!cfg.outlineColor || !cfg.fillColor) return null;
  return { outline: cfg.outlineColor, fill: cfg.fillColor };
}

/**
 * Pick a high-contrast neutral text color given a fill. Uses WCAG relative
 * luminance — dark text on light fills, white text on dark fills. Text color
 * stays neutral (white / #111827 / #4b5563) so the thick border carries the
 * domain's identity, not the word itself.
 */
export function pickTextColor(fillHex: string): string {
  const rgb = hexToRgb(fillHex);
  if (!rgb) return '#111827';
  const l = relativeLuminance(rgb);
  return l > 0.55 ? '#111827' : '#ffffff';
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = hex.replace('#', '').trim();
  if (m.length !== 3 && m.length !== 6) return null;
  const full = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

function relativeLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
  const norm = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * norm[0] + 0.7152 * norm[1] + 0.0722 * norm[2];
}
